import { Box, Chip, TableCell, Typography } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CellData, useOCRStore } from '../store/ocrStore';

interface DataTableCellProps {
    tableId: string;
    rowIndex: number;
    colIndex: number;
    cellRenderers: Record<string, React.FC<any>>;
    usedSensorIds: Set<string>;
    annotationId: string;
}

const DataTableCellComponent: React.FC<DataTableCellProps> = ({
    tableId,
    rowIndex,
    colIndex,
    cellRenderers,
    usedSensorIds,
    annotationId,
}) => {
    const { t } = useTranslation();
    const cell = useOCRStore(state => state.tables[tableId]?.grid[rowIndex]?.[colIndex]) as CellData | null;
    const { updateTableCell, selectedBlockId, setSelectedBlockId } = useOCRStore(state => ({
        updateTableCell: state.updateTableCell,
        selectedBlockId: state.selectedBlockId,
        setSelectedBlockId: state.setSelectedBlockId,
    }));

    const cellRef = useRef<HTMLTableCellElement | null>(null);

    useEffect(() => {
        if (cell?.id === selectedBlockId) {
            cellRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest',
            });
        }
    }, [selectedBlockId, cell?.id]);


    if (!cell || 'spanned' in cell) return null;

    const handleCellClick = () => {
        setSelectedBlockId(selectedBlockId === cell.id ? null : cell.id);
        document.getElementById(`annotation-preview-${annotationId}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    };

    const CellComponent = cellRenderers[cell.type] ?? (() => (
        <Box sx={{ color: 'error.main', fontStyle: 'italic' }}>
            {t('logbookConfig.unknownType', 'Unknown type')}: {cell.type}
        </Box>
    ));

    const isSelected = cell.id === selectedBlockId;
    const isHeader = cell.isHeader ?? false;

    return (
        <TableCell
            ref={cellRef}
            key={cell.id}
            rowSpan={cell.rowSpan}
            colSpan={cell.colSpan}
            onClick={handleCellClick}
            sx={{
                p: 2,
                minWidth: 40,
                cursor: 'pointer',
                position: 'relative',
                transition: 'all 0.2s ease-in-out',
                ...(isHeader && {
                    textAlign: 'center',
                    backgroundColor: 'grey.100',
                    fontWeight: 'bold',
                }),
                ...(isSelected && {
                    backgroundColor: 'rgba(25, 118, 210, 0.3)',
                    border: '2px solid',
                    borderColor: 'primary.main',
                    zIndex: 5,
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'primary.main',
                        opacity: 0.08,
                        pointerEvents: 'none'
                    }
                }),
                '&:hover': {
                    backgroundColor: isSelected ? 'primary.light' : 'action.hover',
                    transform: 'scale(1.01)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }
            }}
        >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
                {isHeader ? (
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 'bold',
                            color: 'text.primary',
                        }}
                    >
                        {String(cell.value)}
                    </Typography>
                ) : (
                    <>
                        <Typography
                            variant="body2"
                            sx={{
                                mb: 1,
                                fontWeight: 500,
                                color: 'text.secondary',
                                fontSize: '0.75rem',
                                height: 20
                            }}
                        >
                            {String(cell.value)}
                        </Typography>
                        <CellComponent
                            key={cell.id}
                            value={cell.sensor}
                            onChange={(val: unknown) => updateTableCell(tableId, rowIndex, colIndex, val)}
                            meta={cell.meta}
                            usedSensorIds={usedSensorIds}
                        />
                        {cell?.sensor?.sensor && (
                            <Chip
                                label={cell.sensor.sensor}
                                sx={{
                                    mt: 1,
                                    py: 1,
                                    fontWeight: 500,
                                    fontSize: '0.65rem',
                                    height: 16,
                                    color: (theme) => theme.palette.primary.main,
                                    backgroundColor: (theme) => theme.palette.primary.contrastText,
                                    '& .MuiChip-label': {
                                        px: 1,
                                    },
                                }}
                            />
                        )}
                    </>
                )}
            </Box>
        </TableCell>
    );
};

export const DataTableCell = React.memo(DataTableCellComponent);